import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useUpsertStatus, type WeeklyStatus } from "@/lib/api";
import { logger } from "@/lib/logger";
import { normalizeRag, type Rag } from "@/lib/rag";

/** ISO date (yyyy-mm-dd) for the Monday of the current week, in LOCAL time. */
function currentWeekStart(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = (day + 6) % 7; // days since Monday
  // L1: build the ISO string from LOCAL date parts so it doesn't shift a day
  // near midnight the way toISOString() (UTC) would.
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
  const year = monday.getFullYear();
  const month = String(monday.getMonth() + 1).padStart(2, "0");
  const date = String(monday.getDate()).padStart(2, "0");
  return `${year}-${month}-${date}`;
}

interface StatusEditorProps {
  trackId: string;
  draft: WeeklyStatus | null;
}

export function StatusEditor({ trackId, draft }: StatusEditorProps) {
  const upsertMutation = useUpsertStatus();

  const [weekStart, setWeekStart] = useState(draft?.week_start ?? currentWeekStart());
  const [rag, setRag] = useState<Rag>(normalizeRag(draft?.rag));
  const [percent, setPercent] = useState(String(draft?.percent_complete ?? 0));
  const [summary, setSummary] = useState(draft?.summary ?? "");
  const [progress, setProgress] = useState(draft?.progress ?? "");
  const [blockers, setBlockers] = useState(draft?.blockers ?? "");
  const [nextSteps, setNextSteps] = useState(draft?.next_steps ?? "");
  const [error, setError] = useState("");

  // Re-seed the form when the editable draft changes (e.g. after a save/publish).
  useEffect(() => {
    setWeekStart(draft?.week_start ?? currentWeekStart());
    setRag(normalizeRag(draft?.rag));
    setPercent(String(draft?.percent_complete ?? 0));
    setSummary(draft?.summary ?? "");
    setProgress(draft?.progress ?? "");
    setBlockers(draft?.blockers ?? "");
    setNextSteps(draft?.next_steps ?? "");
    setError("");
  }, [draft]);

  const handleSave = async () => {
    setError("");
    const parsedPercent = Number(percent);
    if (Number.isNaN(parsedPercent) || parsedPercent < 0 || parsedPercent > 100) {
      setError("Percent complete must be between 0 and 100.");
      return;
    }
    try {
      logger.debug("[tracks]", "upsert weekly status", { trackId, weekStart, rag });
      await upsertMutation.mutateAsync({
        trackId,
        weekStart,
        rag,
        percentComplete: parsedPercent,
        summary: summary || null,
        progress: progress || null,
        blockers: blockers || null,
        nextSteps: nextSteps || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save status.");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{draft ? "Edit draft" : "New weekly status"}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="weekStart">Week starting</Label>
            <Input
              id="weekStart"
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rag">Status</Label>
            <Select value={rag} onValueChange={(value) => setRag(value as Rag)}>
              <SelectTrigger id="rag">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="green">On track</SelectItem>
                <SelectItem value="amber">At risk</SelectItem>
                <SelectItem value="red">Off track</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="percent">Percent complete</Label>
            <Input
              id="percent"
              type="number"
              min={0}
              max={100}
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="summary">Summary</Label>
          <Textarea
            id="summary"
            rows={2}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="progress">Progress</Label>
          <Textarea
            id="progress"
            rows={3}
            value={progress}
            onChange={(e) => setProgress(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="blockers">Blockers</Label>
          <Textarea
            id="blockers"
            rows={2}
            value={blockers}
            onChange={(e) => setBlockers(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="nextSteps">Next steps</Label>
          <Textarea
            id="nextSteps"
            rows={2}
            value={nextSteps}
            onChange={(e) => setNextSteps(e.target.value)}
          />
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={upsertMutation.isPending}>
            {upsertMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
